import { useEffect, useRef, type RefObject } from 'react';
import type { GridApi } from 'ag-grid-community';
import { useI18n } from './i18n';

const refreshes = new WeakMap<GridApi, Promise<void>>();

/** Refresh cached Community filter UI using public APIs, without replacing the grid.
 * Current screens use default auto-apply filters: hiding flushes their pending
 * debounce before the applied model is captured. Explicit Apply-button filters
 * require a separate draft-state adapter and must not use this helper.
 */
export function refreshGridLanguage(api: GridApi): Promise<void> {
  const refresh = (refreshes.get(api) ?? Promise.resolve()).then(async () => {
    if (api.isDestroyed()) return;
    const columns = api.getColumns()?.filter(column => column.isFilterAllowed()) ?? [];
    if (columns.some(column => column.getColDef().filterParams?.buttons?.includes('apply'))) {
      throw new Error('useGridLanguage requires auto-apply filters; add a draft-state adapter before enabling Apply buttons.');
    }
    api.hideColumnFilter();
    const models = api.getFilterModel();
    // Destroying a filter also drops its model. Restore the entire snapshot only
    // after all disposals, before notifying the row model of the restored state.
    for (const column of columns) api.destroyFilter(column);
    await Promise.resolve();
    if (api.isDestroyed()) return;
    await Promise.all(Object.entries(models).map(([key, model]) => api.setColumnFilterModel(key, model)));
    if (api.isDestroyed()) return;
    api.onFilterChanged();
    api.refreshHeader();
    api.refreshCells({ force: true });
  });
  refreshes.set(api, refresh);
  return refresh;
}

/** Accepts either an AgGridReact ref or an existing GridApi ref. */
export function useGridLanguage<T>(reference: RefObject<GridApi<T> | { api: GridApi<T> } | null>) {
  const { language } = useI18n();
  const previous = useRef(language);
  useEffect(() => {
    if (previous.current === language) return;
    previous.current = language;
    const value = reference.current;
    const api = value && ('api' in value ? value.api : value);
    if (api && !api.isDestroyed()) void refreshGridLanguage(api);
  }, [language, reference]);
}
